import React from 'react';

import { AudioContainer, DraggableAudio, useAudioPlayerStore } from '@atlassian/ai-audio';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary';
import { ConditionalCreateSpaceDrawer } from '@confluence/create-space/entry-points/createSpace';
import {
	ConditionalImportContentModal,
	ConditionalImportContentDrawer,
} from '@confluence/import-content/entry-points/importContent';
import { ForgeGlobalKeyboardShortcuts } from '@confluence/forge-ui/entry-points/ForgeGlobalKeyboardShortcuts';
import { StorageEnforcement } from '@confluence/storage-enforcement/entry-points/StorageEnforcement';
import { LoomAiMeetingNotesPostProvisionFlag } from '@confluence/experiment-loom-ai-meeting-notes-in-confluence-cross-flow/entry-points/LoomAiMeetingNotesPostProvisionFlag';
import { RovoVideoModal } from '@confluence/rovo-video-modal/entry-points/RovoVideoModal';
import { TrialFlags } from '@confluence/trial-components/entry-points/TrialFlags';
import { LazyRestrictionsDialogMountPoint } from '@confluence/restrictions/entry-points/RestrictionsDialogTrigger';
import { ProgressBar } from '@confluence/global-progress-bar';
import { useAddUFOCustomDataForNavType } from '@confluence/nav4-enabled/entry-points/useAddUFOCustomDataForNavType';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled';
import { MisalignedNavVersionsFlag } from '@confluence/nav-versions-misaligned';
import { TrialPlanComparisonModalMountComponent } from '@confluence/persistent-upgrade/entry-points/lazyTrialPlanComparisonModal';
import { StandardToPremiumModalMountComponent } from '@confluence/experiment-edition-awareness-for-standard-to-premium';
import { ModernizeChangeboarding } from '@confluence/modernize-changeboarding';
import { ConfluenceSmartCreationModalLoader } from '@confluence/smart-create/entry-points/smartCreationModal';
import { audioFeatures } from '@confluence/audio/entry-points/audioFeatures';
import { CrossJoinNudgesWithFlags } from '@confluence/cross-join-nudges';
import { fg } from '@confluence/feature-gating';
import { UpgradeFreePoolExperimentModalPostUpgradeModalLoadable } from '@confluence/experiment-upgrade-free-pool-target/entry-points/post-upgrade-modal';
import { UpgradeFreePoolExperimentModalPreUpgradeModalLoadable } from '@confluence/experiment-upgrade-free-pool-target/entry-points/pre-upgrade-modal';
/**
 * Use this component to render global components that should be rendered on every page.
 */
export const GlobalComponents = () => {
	const isNav4DisplayedToUser = useIsNav4Enabled();
	const { isOpen: isAudioPlayerOpen } = useAudioPlayerStore();

	useAddUFOCustomDataForNavType();

	return (
		<>
			<ErrorBoundary
				attribution={Attribution.DISCO}
				attributes={{
					errorBoundaryId: 'GlobalComponents-Disco',
				}}
			>
				<ConditionalCreateSpaceDrawer />
				<MisalignedNavVersionsFlag />
			</ErrorBoundary>

			<ErrorBoundary
				attribution={Attribution.SMARTS}
				attributes={{
					errorBoundaryId: 'GlobalComponents-Smarts',
				}}
			>
				{fg('aifc_create_enabled') && <ConfluenceSmartCreationModalLoader />}
			</ErrorBoundary>

			<ErrorBoundary
				attribution={Attribution.CC_IMPORTER}
				attributes={{
					errorBoundaryId: 'GlobalComponents-Importer',
				}}
			>
				<ConditionalImportContentModal />
				<ConditionalImportContentDrawer />
			</ErrorBoundary>

			<ErrorBoundary
				attribution={Attribution.ECOSYSTEM}
				attributes={{
					errorBoundaryId: 'GlobalComponents-Ecosystem',
				}}
			>
				<ForgeGlobalKeyboardShortcuts />
			</ErrorBoundary>

			<ErrorBoundary
				attribution={Attribution.ADMIN_EXPERIENCE}
				attributes={{
					errorBoundaryId: 'GlobalComponents-AdminExperience',
				}}
			>
				<StorageEnforcement />
			</ErrorBoundary>

			<ErrorBoundary
				attribution={Attribution.GROWTH_LUNA}
				attributes={{
					errorBoundaryId: 'GlobalComponents-GrowthLuna',
				}}
			>
				<TrialPlanComparisonModalMountComponent />
				<StandardToPremiumModalMountComponent />
				<RovoVideoModal />
				<TrialFlags />
			</ErrorBoundary>

			<ErrorBoundary attribution={Attribution.PERMISSIONS_EXPERIENCE}>
				<LazyRestrictionsDialogMountPoint />
			</ErrorBoundary>

			<ErrorBoundary attribution={Attribution.FLYWHEEL_ACTIVATION}>
				<ModernizeChangeboarding />
			</ErrorBoundary>

			{isNav4DisplayedToUser && (
				<ErrorBoundary attribution={Attribution.BACKBONE}>
					<ProgressBar isNav4 />
				</ErrorBoundary>
			)}

			<ErrorBoundary
				attribution={Attribution.GROWTH_PUMA}
				attributes={{
					errorBoundaryId: 'GlobalComponents-GrowthPuma',
				}}
			>
				<CrossJoinNudgesWithFlags execNudge={fg('puma-256-cjn-global-home')} />
			</ErrorBoundary>

			{fg('loom_ai_meeting_notes_in_confluence_killswitch') && (
				<ErrorBoundary attribution={Attribution.GROWTH_MULTIPLIERS}>
					<LoomAiMeetingNotesPostProvisionFlag />
				</ErrorBoundary>
			)}

			<ErrorBoundary
				attribution={Attribution.GROWTH_LUNA}
				attributes={{
					errorBoundaryId: 'GlobalComponents-GrowthArtemis',
				}}
			>
				{fg('twcg-206_confluence_upgrade_permanent_free_fg') && (
					<>
						<UpgradeFreePoolExperimentModalPreUpgradeModalLoadable />
						<UpgradeFreePoolExperimentModalPostUpgradeModalLoadable />
					</>
				)}
			</ErrorBoundary>

			{isAudioPlayerOpen && (
				// TODO: hardcoding cloudId and activationId for now
				// once we have the light-weight wrapper we can destructure from sessionData
				<DraggableAudio>
					<AudioContainer
						product="confluence"
						cloudId="a436116f-02ce-4520-8fbb-7301462a1674"
						activationId="a5c7d427-c980-45d1-a98d-97106e40c71d"
						audioFeatures={audioFeatures}
					/>
				</DraggableAudio>
			)}
		</>
	);
};
